/**
 * Where the API lives, for code that might not be running on the same origin as the API.
 *
 * WHY THIS EXISTS. Today the phone apps set `server.url` in capacitor.config.ts, so the
 * app IS growth-sync.com in a WebView and a relative "/api/..." resolves correctly. The
 * cost of that is the whole interface is downloaded over the network on every launch,
 * which is where the white screens and the slow starts come from.
 *
 * Bundling the app instead (what the ProFlow apps do) removes `server.url`. At that point
 * the page is served from the device, there is no origin to be relative to, and every
 * relative "/api/..." breaks. This is the prerequisite for that change.
 *
 * IT IS DELIBERATELY A NO-OP TODAY. On the web it returns the identical relative string,
 * so nothing about the site changes. Inside the current app it returns an absolute URL
 * pointing at the very origin the WebView is already on, so that is unchanged too. It only
 * starts doing real work on the day `server.url` goes away.
 */

/** Overridable so a preview or a local build can point the app somewhere else. */
const API_ORIGIN = process.env.NEXT_PUBLIC_API_ORIGIN || "https://growth-sync.com";

export function apiUrl(path: string): string {
  // Anything already absolute, or a non-path, is returned untouched.
  if (!path.startsWith("/")) return path;

  // On the server a relative path is correct and an absolute one would be a needless
  // round trip out to the internet and back.
  if (typeof window === "undefined") return path;

  /* Ask Capacitor whether this is genuinely a native app, and nothing else.
     NOT isCapacitorApp() from lib/utils: that treats `window.Capacitor !== undefined` as
     proof, and the Capacitor shim is loaded on the plain website too (the splash-screen
     import pulls in @capacitor/core), so it answers TRUE in a desktop browser. Verified in
     Chromium against the dev server: window.Capacitor defined, getPlatform() "web",
     isNativePlatform() false. Trusting it here would have rewritten every API call on
     localhost and on preview deploys to the PRODUCTION origin, pointing development
     traffic, writes included, at real data. */
  const capacitor = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  const isNative = typeof capacitor?.isNativePlatform === "function" && capacitor.isNativePlatform();
  if (!isNative) return path;

  return `${API_ORIGIN}${path}`;
}
